import { useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { colors } from '../../utils/colors';
import { useFocusTrap, useEscapeKey, useClickOutside } from '../../utils/accessibility';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${colors.overlay};
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 1rem;
  z-index: 1000;
  backdrop-filter: blur(4px);
  overflow-y: auto;
  
  @media (min-width: 768px) {
    align-items: center;
  }
`;

const ModalContent = styled(motion.div)`
  background: ${colors.white};
  border-radius: 15px;
  width: 100%;
  max-width: 700px;
  position: relative;
  box-shadow: 0 10px 30px ${colors.shadow};
  margin: 1rem 0;
  max-height: calc(100vh - 2rem);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  
  @media (max-width: 767px) {
    max-width: 500px;
    margin: 0.5rem 0;
    max-height: calc(100vh - 1rem);
    border-radius: 15px 15px 0 0;
  }
`;

const ModalHeader = styled.div`
  padding: 1.5rem 2rem;
  border-bottom: 1px solid ${colors.border};
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;

  h2 {
    margin: 0;
    font-size: 1.5rem;
    color: ${colors.textDark};
    font-weight: 600;
  }
  
  @media (max-width: 767px) {
    padding: 1rem 1.5rem;
    
    h2 {
      font-size: 1.25rem;
    }
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${colors.textLight};
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  margin: -0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;

  &:hover {
    color: ${colors.textDark};
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${colors.primaryLight};
    border-radius: 4px;
  }
`;

const ModalBody = styled.div`
  padding: 2rem;
  overflow-y: auto;
  flex: 1;
  
  @media (max-width: 767px) {
    padding: 1.5rem;
  }
`;

export const Modal = ({ isOpen, onClose, title, children, ariaLabel }) => {
  const modalRef = useFocusTrap(isOpen);
  useClickOutside(modalRef, onClose);
  
  useEscapeKey(useCallback(() => {
    if (isOpen) onClose();
  }, [isOpen, onClose]));

  if (!isOpen) return null;

  return createPortal(
    <ModalOverlay
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel || title}
    >
      <ModalContent
        ref={modalRef}
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.2 }}
        role="document"
      >
        <ModalHeader>
          <h2 id="modal-title">{title}</h2>
          <CloseButton
            onClick={onClose}
            aria-label="Close modal"
          >
            ×
          </CloseButton>
        </ModalHeader>
        <ModalBody>
          {children}
        </ModalBody>
      </ModalContent>
    </ModalOverlay>,
    document.body
  );
};
